import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  type BoxProps,
} from "@chakra-ui/react";
import { motion, PanInfo } from "framer-motion";

type BottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
} & { backgroundColor?: BoxProps["backgroundColor"] };

function Dragger() {
  return (
    <Flex justifyContent="center" alignItems="center" p="1rem">
      <Box w="2rem" h="0.25rem" bgColor="#79747E" borderRadius="sm" />
    </Flex>
  );
}

function ExtraSpaceForElasticity() {
  // This box adds some extra space at the bottom of the BottomSheet to allow
  // showing the elastic drag effect without showing a blank area.
  // Make it taller when increasing the dragElastic value.
  return (
    <Box h="50vh" w="100%" bgColor="white" position="absolute" top="calc(100% - 1px)" left="0" />
  );
}

export function BottomSheet({ isOpen, onClose, children, ...rest }: BottomSheetProps) {
  const swipeConfidenceThreshold = 5000;
  const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity;

  const onDrag = async (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info;
    const swipe = swipePower(offset.y, velocity.y);
    if (offset.y > 0 && swipe > swipeConfidenceThreshold) onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        as={motion.div}
        drag="y"
        // @ts-ignore Framer Motion's onDragEnd signature is different from Chakra's
        onDragEnd={onDrag}
        dragElastic={0.1}
        dragConstraints={{ top: 0 }}
        w="100vw"
        maxW="22.5rem"
        h="max-content"
        roundedTop="1.75rem"
        alignSelf="flex-end"
        {...rest}
      >
        <Dragger />
        {/* Content box to have a maximum height and show a scroll when content is too long */}
        {/* Max Height = Viewport height - marginTop (72 px) - draggerHeight (36 px) */}
        <Box maxH="calc(100vh - 4.5rem - 2.25rem)" overflowY="auto">
          {children}
        </Box>
        <ExtraSpaceForElasticity />
      </DrawerContent>
    </Drawer>
  );
}

BottomSheet.Header = DrawerHeader;
BottomSheet.Body = DrawerBody;
BottomSheet.Footer = DrawerFooter;
